import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import "../../components/CustomComponents/Switchery/ToggleSwitch.css";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader.js";

const MASKED = "••••••••";

const ManageSubscriptionDojoConfig = () => {
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    hostURL: "https://api.dojo.tech",
    apiVersion: "2024-02-05",
    apiPriKey: "",
    apiSecKey: "",
    hasAPIPriKey: false,
    hasAPISecKey: false,
    active: true,
  });

  const labelColor = isDarkMode ? "#ffffff" : "#000000";
  const muted = isDarkMode ? "#adb5bd" : "#6c757d";

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.SA_GET_SUBSCRIPTION_DOJO_CONFIG);
      if (response.data.status) {
        const data = response.data.data || {};
        const hasPri = !!data.hasAPIPriKey;
        const hasSec = !!data.hasAPISecKey;
        setForm({
          hostURL: data.hostURL || "https://api.dojo.tech",
          apiVersion: data.apiVersion || "2024-02-05",
          apiPriKey: hasPri ? MASKED : "",
          apiSecKey: hasSec ? MASKED : "",
          hasAPIPriKey: hasPri,
          hasAPISecKey: hasSec,
          active: data.active !== false,
        });
      } else {
        showErrorToast(response.data.message || "Failed to load config");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.hostURL?.trim()) {
      showErrorToast("Host URL is required");
      return;
    }
    const pri = (form.apiPriKey || "").trim();
    const sec = (form.apiSecKey || "").trim();
    if (!form.hasAPIPriKey && (!pri || pri === MASKED)) {
      showErrorToast("Primary Key (API secret) is required");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        HostURL: form.hostURL.trim(),
        ApiVersion: form.apiVersion?.trim() || "",
        APIPriKey: pri === MASKED ? "" : pri,
        APISecKey: sec === MASKED ? "" : sec,
        Active: form.active,
      };
      const response = await axiosInstance.put(
        APIs.SA_SAVE_SUBSCRIPTION_DOJO_CONFIG,
        payload
      );
      if (response.data.status) {
        showSuccessToast(response.data.message || "Saved");
        await fetchConfig();
      } else {
        showErrorToast(response.data.message || "Failed to save");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Subscription Dojo</li>
          </ol>
          <h1 className="page-header mb-0">Subscription Dojo</h1>
          <p className="mb-0 mt-1" style={{ color: muted, fontSize: "0.9rem" }}>
            Platform Dojo keys for SaaS subscription invoices. Separate from restaurant payment
            gateways. Keys are encrypted at rest.
          </p>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            disabled={saving || loading}
            onClick={handleSave}
            style={{
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            {saving ? (
              <>
                <i className="fa fa-spinner fa-spin me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fa fa-save me-2" />
                Save
              </>
            )}
          </button>
        </div>
      </div>

      <GridSectionLoader loading={loading} minHeight="240px">
        <div className="card">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-8">
                <FormInput
                  label="Host URL"
                  name="hostURL"
                  type="text"
                  value={form.hostURL}
                  placeHolder="https://api.dojo.tech"
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  label="API Version"
                  name="apiVersion"
                  type="text"
                  value={form.apiVersion}
                  placeHolder="2024-02-05"
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label={
                    form.hasAPIPriKey
                      ? "Primary Key (API secret) — saved"
                      : "Primary Key (API secret)"
                  }
                  name="apiPriKey"
                  type="password"
                  value={form.apiPriKey}
                  placeHolder="Dojo API secret key"
                  onchange={handleInputChange}
                />
                <small style={{ color: muted }}>
                  Leave masked value unchanged to keep the existing key.
                </small>
              </div>
              <div className="col-md-6">
                <FormInput
                  label={
                    form.hasAPISecKey
                      ? "Webhook signing secret — saved"
                      : "Webhook signing secret"
                  }
                  name="apiSecKey"
                  type="password"
                  value={form.apiSecKey}
                  placeHolder="Dojo webhook signing secret"
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label" style={{ color: labelColor }}>
                  Status
                </label>
                <ToggleSwitch
                  isChecked={form.active}
                  onToggle={() => setForm((prev) => ({ ...prev, active: !prev.active }))}
                  label={form.active ? "Active" : "Inactive"}
                />
              </div>
            </div>
          </div>
        </div>
      </GridSectionLoader>
    </div>
  );
};

export default ManageSubscriptionDojoConfig;
